/**
 * PermitCard Component
 * Displays a single permit with status badge and key information
 * Phase 6 Batch 2 - Permits Frontend UI
 * Phase 8.2 - Added permit renewal support
 * 9.9a - Added color-coded status indicators
 */
import { Permit } from '@/types/permits';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2, RefreshCw } from 'lucide-react';
import { differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';

interface PermitCardProps {
  permit: Permit;
  onEdit: (permit: Permit) => void;
  onDelete: (permitId: string) => void;
  onRenew?: (permit: Permit) => void;
  isInternalMilestone?: boolean;
}

const statusConfig = {
  not_submitted: { label: 'Not Started', className: 'bg-gray-200 text-gray-800' },
  submitted: { label: 'Submitted', className: 'bg-blue-500 text-white' },
  issued: { label: 'Issued', className: 'bg-green-500 text-white' },
  closed: { label: 'Closed', className: 'bg-slate-600 text-white' }
} as const;

/**
 * 9.9a: Get card border/background color based on permit status and due date urgency.
 * Priority: Overdue/Due soon > Status > Default
 *
 * Color meanings:
 * - Red: Urgent (overdue or due within 30 days)
 * - Orange: Warning (due 30-60 days or in progress)
 * - Green: Good (complete)
 * - Gray: Neutral (not started)
 */
function getPermitCardColorClass(permit: Permit): string {
  const daysUntilDue = permit.due_date
    ? differenceInDays(new Date(permit.due_date), new Date())
    : null;

  // Priority 1: Due date warnings (most urgent)
  if (daysUntilDue !== null) {
    if (daysUntilDue < 0) {
      return 'border-red-500 bg-red-50';          // Red - OVERDUE
    }
    if (daysUntilDue <= 30) {
      return 'border-red-500 bg-red-50';          // Red - Due soon (<30 days)
    }
    if (daysUntilDue <= 60) {
      return 'border-orange-500 bg-orange-50';    // Orange - Due soon (30-60 days)
    }
  }

  // Priority 2: Status-based colors (if not due soon)
  switch (permit.status) {
    case 'not_submitted':
      return 'border-gray-300 bg-gray-50';        // Gray - not started
    case 'submitted':
      return 'border-orange-500 bg-orange-50';    // Orange - in progress
    case 'issued':
    case 'closed':
      return 'border-green-500 bg-green-50';      // Green - complete
    default:
      return 'border-gray-300 bg-gray-50';
  }
}

export function PermitCard({ permit, onEdit, onDelete, onRenew, isInternalMilestone = false }: PermitCardProps) {
  const { label, className } = statusConfig[permit.status];

  // Phase 8.2: Determine if permit can be renewed (not applicable for milestones)
  const isRenewable = Boolean(
    !isInternalMilestone &&
    permit.recurring_period &&
    !permit.renewed_by_permit_id &&
    onRenew
  );
  const isRenewed = Boolean(permit.renewed_by_permit_id);

  return (
    <Card
      className={cn(
        "p-4 hover:shadow-md transition-all cursor-pointer",
        isInternalMilestone ? 'border-purple-200 bg-purple-50' : getPermitCardColorClass(permit)
      )}
      data-testid="permit-card"
      onClick={() => onEdit(permit)}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h4 className="font-medium">{permit.permit_type}</h4>
            {isInternalMilestone ? (
              <Badge className="bg-purple-100 text-purple-700 border-purple-200">Milestone</Badge>
            ) : (
              <Badge className={className}>{label}</Badge>
            )}
            {isRenewed && (
              <Badge className="bg-purple-500 text-white">Renewed</Badge>
            )}
          </div>

          <div className="text-sm text-muted-foreground space-y-1">
            {permit.case_number && (
              <p>Case #: {permit.case_number}</p>
            )}
            {permit.permit_number && (
              <p>Reference #: {permit.permit_number}</p>
            )}
            {permit.date_submitted && (
              <p>Submitted: {new Date(permit.date_submitted).toLocaleDateString()}</p>
            )}
            {permit.date_issued && (
              <p>Issued: {new Date(permit.date_issued).toLocaleDateString()}</p>
            )}
            {/* Only show expiry/renewal fields for real permits, not milestones */}
            {!isInternalMilestone && permit.due_date && (
              <p>
                Due: {new Date(permit.due_date).toLocaleDateString()}
                {permit.recurring_period && ` (${permit.recurring_period})`}
              </p>
            )}
            {!isInternalMilestone && permit.fees_costs && (
              <p>Fees: ${permit.fees_costs.toLocaleString()}</p>
            )}
            {/* V052: Show who is handling permit (9.9e, 9.10) */}
            {permit.applicant_organization_name && (
              <p className="italic">Handled by: {permit.applicant_organization_name}</p>
            )}
          </div>
        </div>

        <div className="flex gap-1">
          {isRenewable && (
            <Button
              variant="ghost"
              size="icon"
              data-testid="renew-permit"
              onClick={(e) => {
                e.stopPropagation();
                onRenew!(permit);
              }}
              title="Renew permit"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            data-testid="delete-permit"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(permit.id);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
